import * as _ from 'lodash';
import { useCallback, useMemo, type VoidFunctionComponent } from 'react';

import { Action } from '../../../../../../../typings/permissions';
import { useDresser } from '../../../../../../contexts/DrawerContext';
import { window } from '../../../../../../globals';
import useIsLicenseConcludedEnabled from '../../../../../../hooks/features/useIsLicenseConcludedEnabled';
import useHasProjectPermission from '../../../../../../shared/permissions/useHasProjectPermission';
import useHasReleaseGroupPermission from '../../../../../../shared/permissions/useHasReleaseGroupPermission';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button';
import { icons } from '../../../../../generic/Icon';
import type { Dependency, DependencyIssue } from '../../../../../../../shared/dependenciesV2';
import type { LicenseGroup } from '../../../../../../../shared/licenses';
import type { ConclusionData } from '../../../../../../../shared/types/licenses';

import { AddLicense } from './AddLicense';
import { ConclusionBanner } from './ConclusionBanner';
import DependencyLicensesTable from './DependencyLicensesTable';
import type { LicenseIssueTypeMap, LicenseTabScope } from './types';

import drawerStyles from '../../DependencyDrawer.module.less';
import styles from './DependencyLicenses.module.less';

type Props = {
  issues: Array<Omit<DependencyIssue, 'notes'>>;
  licenseGroups: LicenseGroup[];
  concludedLicenses: ConclusionData;
  scope: LicenseTabScope;
  isLoading: boolean;
  dependency: Pick<Dependency, 'title' | 'version' | 'locator'>;
  /**
   * defaults to false
   */
  shouldAllowEditing?: boolean;
  /**
   * defaults to true
   */
  isLicenseScanningEnabled?: boolean;
};

const DependencyLicenses: VoidFunctionComponent<Props> = ({
  issues,
  concludedLicenses,
  licenseGroups,
  scope,
  isLoading,
  dependency,
  shouldAllowEditing = false,
  isLicenseScanningEnabled = true,
}) => {
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);
  const isProjectScope = scope.type === 'project';
  const isReleaseGroupScope = scope.type === 'releaseGroup';

  const hasProjectPermission = useHasProjectPermission(
    isProjectScope ? scope.projectLocator : '',
    Action.Edit,
  );
  const hasReleaseGroupPermission = useHasReleaseGroupPermission(
    isReleaseGroupScope ? scope.releaseGroupId : -1,
    Action.Edit,
  );
  const hasPermission =
    (isProjectScope && hasProjectPermission) ||
    (isReleaseGroupScope && hasReleaseGroupPermission);

  const canEditLicenses =
    shouldAllowEditing && hasPermission;

  const { pushDrawer } = useDresser();

  const baseConclusions = concludedLicenses.base;
  const scopedConclusions = concludedLicenses.scoped;

  const onAddLicense = useCallback((licenseGroup?: LicenseGroup) => {
    pushDrawer(<AddLicense
      licenseGroup={licenseGroup}
      dependencyRevisionLocator={dependency.locator}
      parentRevisionLocator={scope.type === 'project' ? scope.revisionLocator : undefined}
    />);
  }, [dependency.locator, pushDrawer, scope]);

  const licenseIssueTypeMap: LicenseIssueTypeMap = useMemo(() => {
    return issues.reduce(
      (acc, issue) => {
        if (issue.license) {
          acc[issue.license] = { type: issue.type, isIgnored: issue.status === 'ignored' };
        }
        return acc;
      },
      {} as LicenseIssueTypeMap,
    );
  }, [issues]);

  const allLicenses = new Set<string>(
    licenseGroups.flatMap((group) => group.licenses.map((license) => license.licenseId)),
  );

  // Separate the license groups into declared and discovered.
  const [declaredLicenseGroups, discoveredLicenseGroups] = useMemo(() =>
    _.partition(licenseGroups, (group) => group.isDeclared), [licenseGroups]);

  return (
    <>
      {isLicenseConcludedEnabled && baseConclusions && (
        <ConclusionBanner
          dependencyRevisionLocator={dependency.locator}
          scope={scope}
          concludedLicenses={concludedLicenses}
          allLicenses={allLicenses}
          canEditLicenses={canEditLicenses}
        />
      )}
      <div className={styles.dependencyLicensesContainer}>
        {/* Declared Licenses Section */}
        <div className={styles.licenseTableContainer}>
          <div className={drawerStyles.header}>Declared Licenses</div>
          <p className={styles.sectionDescription}>
            The licenses the author has licensed this package under. When only one declared license is found it is
            automatically the concluded license. You can choose the license to use if more than one is available.
            Alternatively you can manually add a license if needed.
          </p>
          <DependencyLicensesTable
            dependency={dependency}
            licenseGroups={declaredLicenseGroups}
            concludedLicenses={scopedConclusions.licenses}
            licenseIssueTypeMap={licenseIssueTypeMap}
            licenseType="declared"
            scope={scope}
            isLoading={isLoading}
            canEditLicenses={canEditLicenses}
            onAddLicense={onAddLicense}
            isLicenseConcludedEnabled={isLicenseConcludedEnabled}
            isLicenseScanningEnabled={isLicenseScanningEnabled}
          />
          {canEditLicenses && (
            <Button
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={() => {
                onAddLicense(undefined);
              }}
              icon={icons.plusCircleO}
            >
              Add a license group
            </Button>
          )}
        </div>
        {/* Discovered Licenses Section */}
        <div className={styles.licenseTableContainer}>
          <h3 className={styles.sectionTitle}>Discovered Licenses</h3>
          <p className={styles.sectionDescription}>Licenses found bundled within the code of this package.</p>
          <DependencyLicensesTable
            dependency={dependency}
            licenseGroups={discoveredLicenseGroups}
            concludedLicenses={scopedConclusions.licenses}
            licenseIssueTypeMap={licenseIssueTypeMap}
            licenseType="discovered"
            scope={scope}
            isLoading={isLoading}
            canEditLicenses={canEditLicenses}
            onAddLicense={onAddLicense}
            isLicenseConcludedEnabled={isLicenseConcludedEnabled}
            isLicenseScanningEnabled={isLicenseScanningEnabled}
          />
        </div>
      </div>
    </>
  );
};

export default DependencyLicenses;
